import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEOHead } from '../components/SEO';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DynamicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/pages/${slug}`);
        setPage(response.data);
        setError(null);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Page not found');
        } else {
          setError('Failed to load page');
        }
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPage();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-[60vh] text-center px-4">
          <h1 className="font-serif text-4xl text-foreground mb-4">Page Not Found</h1>
          <p className="text-muted-foreground mb-8">The page you're looking for doesn't exist or has been moved.</p>
          <button 
            onClick={() => navigate('/')}
            className="btn-emerald px-6 py-3 rounded-sm"
          >
            Return Home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // Find hero section if exists
  const heroSection = page.sections?.find(s => s.type === 'hero');
  const contentSections = page.sections?.filter(s => s.type !== 'hero') || [];

  return (
    <div className="min-h-screen bg-background" data-testid={`dynamic-page-${slug}`}>
      <SEOHead 
        title={page.meta_title || page.title}
        description={page.meta_description || page.description}
      />
      <Navbar />
      
      {/* Hero Section */}
      {heroSection && (
        <section className="relative h-[40vh] min-h-[350px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-900 to-emerald-950" />
          <div className="relative z-10 text-center text-white px-4">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="overline text-gold mb-3"
            >
              {heroSection.content || 'The Hotel Emerald'}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-serif text-4xl md:text-5xl lg:text-6xl"
            >
              {heroSection.title || page.title}
            </motion.h1>
          </div>
        </section>
      )}

      {/* If no hero, show simple title */}
      {!heroSection && (
        <section className="pt-32 pb-12 bg-gradient-to-b from-emerald-50 to-background dark:from-emerald-950/30 dark:to-background">
          <div className="container-custom">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-serif text-4xl md:text-5xl text-foreground text-center"
            >
              {page.title}
            </motion.h1>
            {page.description && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-muted-foreground text-center mt-4 max-w-2xl mx-auto"
              >
                {page.description}
              </motion.p>
            )}
          </div>
        </section>
      )}

      {/* Content Sections */}
      <section className="section-padding">
        <div className="container-custom max-w-4xl">
          {contentSections.map((section, index) => (
            <motion.div
              key={section.id || index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="mb-12"
            >
              {section.title && section.type !== 'hero' && (
                <h2 className="font-serif text-2xl md:text-3xl text-foreground mb-6">
                  {section.title}
                </h2>
              )}
              
              {section.type === 'text' && section.content && (
                <div className="prose prose-emerald dark:prose-invert max-w-none">
                  <ReactMarkdown
                    components={{
                      h1: ({node, ...props}) => <h1 className="font-serif text-3xl text-foreground mb-4" {...props} />,
                      h2: ({node, ...props}) => <h2 className="font-serif text-2xl text-foreground mb-3 mt-8" {...props} />,
                      h3: ({node, ...props}) => <h3 className="font-serif text-xl text-foreground mb-2 mt-6" {...props} />,
                      p: ({node, ...props}) => <p className="text-muted-foreground mb-4 leading-relaxed" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc list-inside text-muted-foreground mb-4 space-y-2" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal list-inside text-muted-foreground mb-4 space-y-2" {...props} />,
                      li: ({node, ...props}) => <li className="text-muted-foreground" {...props} />,
                      strong: ({node, ...props}) => <strong className="font-semibold text-foreground" {...props} />,
                      a: ({node, ...props}) => <a className="text-emerald-600 hover:text-emerald-700 underline" {...props} />,
                      blockquote: ({node, ...props}) => (
                        <blockquote className="border-l-4 border-emerald-500 pl-4 italic text-muted-foreground my-4" {...props} />
                      ),
                    }}
                  >
                    {section.content.replace(/\\n/g, '\n')}
                  </ReactMarkdown>
                </div>
              )}

              {section.type === 'image' && section.image && (
                <div className="rounded-sm overflow-hidden">
                  <img 
                    src={section.image} 
                    alt={section.title || 'Page image'} 
                    className="w-full h-auto"
                  />
                </div>
              )}

              {section.type === 'features' && section.content && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {section.content.split('|').map((feature, i) => (
                    <div key={i} className="p-6 bg-muted/30 rounded-sm">
                      <p className="font-medium text-foreground">{feature.trim()}</p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          ))}

          {/* Fallback if no sections */}
          {contentSections.length === 0 && page.description && (
            <div className="prose prose-emerald dark:prose-invert max-w-none">
              <p className="text-muted-foreground leading-relaxed">{page.description}</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
